import { useEffect, useState } from 'react'
import type { FormEvent } from 'react'

import { AppLink } from '~/components/AppLink'
import { GoogleButton } from '~/components/GoogleButton'

import s from './Login.module.css'

/**
 * The sign-in page.
 *
 * **Signing in and joining, through one button.** "Continue with Google" is both: an address
 * this site knows signs in, and one it does not is sent to `/join` to choose a name. There is
 * no separate "create an account" control because there is nothing for it to do that this
 * does not already do, and a second button would have to explain the difference to somebody
 * who does not have one.
 *
 * There is no invite-code field, and no invitation is required. A shared code came first —
 * one string for the whole door, forwarded once and untakeable back without locking out
 * everybody holding it — then invitations replaced it and became the only way in, which was
 * the same shut door with better manners. An invitation now welcomes somebody and records who
 * brought them; it does not decide whether they may exist here.
 *
 * `_logo_tag` picked between an `<img>` and an `<h1>` on the server, from whether a wordmark
 * file exists. Here the img is rendered and the h1 replaces it if `/logo` does not answer
 * with a picture — the same two outcomes, decided a moment later.
 */

/**
 * Where to go once you are in.
 *
 * A person pressing a heart on `/e/<id>` was pointed here by that page and should land back
 * on it, with the gig still on the screen — not on the front page, wondering what they had
 * been looking at.
 *
 * **Same origin only, and the origin is asked for rather than pattern-matched.** Two tests
 * on the string — begins with `/`, does not begin with `//` — caught `//evil.example.com`
 * and missed its sibling: a browser reads a leading slash-backslash pair the same way it
 * reads two slashes, so `/\evil.example.com` is another host wearing a leading slash and an
 * open redirect off the back of a login. Resolving the text and comparing `origin` is the
 * only check that cannot be spelled around, because it asks the same parser the navigation
 * will use. Anything that leaves this origin is dropped rather than argued with.
 *
 * Exported for the test, which is the only place the hostile spellings are written down.
 */
export function nextUrl(search: string, origin: string): string {
  const asked = new URLSearchParams(search).get('next') || ''
  let where: URL
  try {
    where = new URL(asked, origin)
  } catch {
    return '/'
  }
  if (where.origin !== origin) return '/'
  return where.pathname + where.search + where.hash
}

/**
 * What the Google leg can come back saying, in English.
 *
 * The server sends a code rather than a sentence, and this page turns it into one. The
 * temptation is to have the server send the sentence and print whatever arrives — one
 * voice, no duplication — but `/login?trouble=<anything>` is then a way to make this site
 * display any text a link wants it to, over its own logo, on the page where people type
 * passwords. A fixed list cannot be talked into saying something it does not contain.
 */
const TROUBLE: Record<string, string> = {
  'google-not-set-up': 'signing in with Google is not set up here',
  'that-sign-in-went-stale': 'that sign-in went stale — try again',
  'google-could-not-be-reached': 'Google could not be reached',
  'that-google-account-is-not-verified': 'Google has not verified that address',
  'no-account-for-that-address': 'there is no account for that address',
  // Google vouched for an address this site holds on an account it has not itself verified.
  // The server refuses to attach on Google's word alone, and says so here rather than
  // dropping the connection.
  'that-address-signs-in-with-a-password':
    'that address already has an account — sign in with its password',
}

export function Login() {
  const [username, setUsername] = useState('')
  const [password, setPassword] = useState('')
  const [error, setError] = useState('')
  const [wordmark, setWordmark] = useState(true)
  const [joining, setJoining] = useState(false)
  // The address a letter actually went to, which is not the same as what is in the box now.
  // Naming the live input meant correcting a typo *after* pressing join rewrote the message
  // to "check <the corrected address>" for a letter that went to the typo — and the join
  // button is hidden once `written` is set, so there was no way to send another without
  // knowing to reload. Which is the failure this whole flow was made loud to avoid.
  const [writtenTo, setWrittenTo] = useState('')
  const [code, setCode] = useState('')
  const [checking, setChecking] = useState(false)
  const [googleOffered, setGoogleOffered] = useState(false)
  // Whether the address form has been sent. The page then says to go and read the letter, and
  // stops offering to send another — a second press is a second letter, the server counts them
  // against a budget, and the fourth one in an hour is silently dropped — three go. Somebody pressing
  // again because nothing arrived would be spending the allowance that would let them retry
  // later.
  const [written, setWritten] = useState(false)


  /*
   * Whether there is a Google button to draw. A bundle is one file for every deployment and
   * cannot know at build time whether this install has credentials — and a button that leads
   * to "not set up" is worse than no button. Failing quietly is the right failure: no
   * button, and the form below still works.
   */
  useEffect(() => {
    let live = true
    fetch('/auth/options', { credentials: 'same-origin' })
      .then(answer => (answer.ok ? answer.json() : { google: false }))
      .then((options: { google?: boolean }) => {
        if (live) setGoogleOffered(Boolean(options.google))
      })
      .catch(() => {})
    return () => {
      live = false
    }
  }, [])

  /*
   * Why the last attempt did not work, when the reader was sent back here from the Google
   * leg rather than from this form. Read once on arrival: it belongs to the navigation that
   * landed here, and re-reading it would put the message back after the next failed attempt
   * had already replaced it.
   */
  useEffect(() => {
    const code = new URLSearchParams(window.location.search).get('trouble')
    if (code) setError(TROUBLE[code] ?? 'that did not work')
  }, [])


  async function go(path: string) {
    setError('')
    /*
     * Not through `api` in `src/api/client.ts`, and this is the one call on the site that
     * should not be. That client turns a 401 into `NeedsSignIn` and sends the reader to
     * this page — which, from this page, is a loop, and the 401 here is not a missing
     * session but a wrong password, the single most ordinary thing that happens on a login
     * form. It also insists on a JSON body, and these two routes answer `text/plain`: the
     * message shown below is the server's own sentence, read as text.
     */
    const answer = await fetch(path, {
      method: 'POST',
      credentials: 'same-origin',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ username, password }),
    })
    if (answer.ok) {
      // A whole navigation, not a route change: the session cookie has just been set and
      // everything the app knows about who is asking was decided before it existed.
      window.location.href = nextUrl(window.location.search, window.location.origin)
      return
    }
    setError((await answer.text()) || 'that did not work')
  }

  function signIn(event: FormEvent<HTMLFormElement>) {
    event.preventDefault()
    void go('/login')
  }

  /*
   * Joining takes the same two fields, which is why it is the same form and not a second page:
   * the difference is which button was pressed, and a page that asked for an address and a
   * password twice would be asking the same question twice.
   *
   * The answer says nothing about whether the address is already an account — that is
   * deliberate on the server's side, and repeating it here is what keeps it true. So the page
   * says a letter is on its way whatever happened, which is also what the server did.
   */
  async function join() {
    // Guarded, because there are only three letters an hour to any address and a double press
    // spends two of them — the same allowance the "check your inbox" message exists to protect.
    if (joining) return
    setJoining(true)
    setError('')
    const answer = await fetch('/signup', {
      method: 'POST',
      credentials: 'same-origin',
      headers: { 'Content-Type': 'application/json' },
      // The address alone. The password is chosen on the page the code leads on to,
      // where whoever answers has proved the mailbox is theirs — anybody can type an address
      // they do not own into this form, so a password set here would be a password set for
      // somebody else's mailbox.
      body: JSON.stringify({ email: username }),
    })
    setJoining(false)
    if (answer.ok) {
      setCode('')
      setWrittenTo(username)
      setWritten(true)
      return
    }
    setError((await answer.text()) || 'that did not work')
  }

  /* The code, checked against the registration this browser holds. On success the server has
     marked the address proved, and `/join` is where the name and the password are chosen. */
  async function offerCode() {
    if (checking) return
    setChecking(true)
    setError('')
    const answer = await fetch('/joining/code', {
      method: 'POST',
      credentials: 'same-origin',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ code }),
    })
    setChecking(false)
    if (answer.ok) {
      window.location.href = '/join'
      return
    }
    setError((await answer.text()) || 'that code is not right')
  }

  return (
    <div className={s.page}>
      <div className={s.sky} aria-hidden="true" />
      <main className={s.main}>
        {wordmark ? (
          <img
            className={s.logo}
            src="/logo"
            alt="London gigs"
            onError={() => setWordmark(false)}
          />
        ) : (
          <h1>London gigs</h1>
        )}

        <form className={s.form} autoComplete="on" onSubmit={signIn}>
          {/* Empty rather than absent, so `.err:empty` closes the gap and the message does
              not push the form down the page when it arrives. */}
          <p className={s.err}>{error}</p>
          {/*
            The code, asked for here rather than opened from a link. A link is a bearer token
            and can be forwarded — register an address you own, read your own letter, send
            somebody the link, and they make an account whose address is yours. A code is
            checked against the registration *this browser* is holding, and a reader's browser
            is holding their own, so there is nothing worth forwarding.

            Read the letter wherever is convenient and type six digits into the tab that is
            already open, which is the same walk a link asked for and one fewer surprise.
          */}
          {written ? (
            <div className={s.sent}>
              <p>We sent a code to {writtenTo}. Type it in to carry on.</p>
              <label htmlFor="c">Code</label>
              <input
                id="c"
                name="code"
                inputMode="numeric"
                autoComplete="one-time-code"
                autoFocus
                value={code}
                onChange={event => setCode(event.target.value.trim())}
              />
              <button
                type="button"
                className={s.button}
                disabled={checking || code.length < 6}
                onClick={() => void offerCode()}
              >
                {checking ? 'One moment' : 'Continue'}
              </button>
            </div>
          ) : null}

          {googleOffered ? (
            <>
              {/*
                An anchor and not a button: this leaves the site, and a whole navigation is
                what it is. `next` is carried so that pressing a heart on a gig lands back on
                that gig — the server checks it again on the way home, because the round trip
                does not pass back through this page.
              */}
              <GoogleButton
                href={`/auth/google?next=${encodeURIComponent(
                  nextUrl(window.location.search, window.location.origin),
                )}`}
              />
              <p className={s.or}>
                <span>or</span>
              </p>
            </>
          ) : null}

          <label htmlFor="u">Email or username</label>
          <input
            id="u"
            name="username"
            autoComplete="username"
            autoCapitalize="none"
            required
            value={username}
            onChange={event => {
              setUsername(event.target.value)
              // Changing the address means the message above is about a different one, so it
              // goes and the join button comes back. Without this a typo was recoverable only
              // by knowing to reload the page.
              setWritten(false)
            }}
          />

          <label htmlFor="p">Password</label>
          <input
            id="p"
            name="password"
            type="password"
            autoComplete="current-password"
            required
            value={password}
            onChange={event => setPassword(event.target.value)}
          />

          <button type="submit" className={s.button}>
            Sign in
          </button>

          {/*
            The other door, and it takes only the address — which is why it is a second button
            beside the same form rather than a second page. `type="button"` so it does not
            submit: the form's submit is signing in.

            The password box above is ignored when joining, and that is deliberate rather than
            sloppy: whoever types an address here has not yet shown it is theirs, so a password
            chosen at this point would be a password chosen for a mailbox that may belong to
            somebody else. It is asked for on the step after the code.

            "First time?" rather than "Create an account", because that is the question
            somebody is actually asking themselves when neither button is obviously theirs.
          */}
          {written ? null : (
            <p className={s.join}>
              First time?{' '}
              <button
                type="button"
                className={s.link}
                disabled={joining}
                onClick={() => void join()}
              >
                Join with this address
              </button>
            </p>
          )}

          {/*
            Not in the Python, and the reason is in the report: this page was a dead end. It
            had no link off it in any direction — the wordmark above is a picture and not a
            way home, and the way here is usually a redirect. Somebody who arrives by
            accident, or who decides not to sign in after all, could only use the back button
            or retype the address.

            On the card rather than under it: centring the page vertically inside the frame
            put this line on a chrome highlight, and pale type on a pale reflection is
            unreadable. The card is the part of this page with a background of its own.
          */}
          <p className={s.back}>
            <AppLink className={s.home} to="/">
              Back to the gigs
            </AppLink>
          </p>
        </form>
      </main>
    </div>
  )
}
